/**
 * The single place the dashboard talks to the backend.
 *
 * Auth is a bearer token from the password login, kept in localStorage. A 401 clears it and
 * notifies listeners, which is how the app falls back to the login screen from anywhere —
 * including from inside a React Query refetch.
 */

const TOKEN_KEY = 'rentx.token';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export const auth = {
  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  set(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    listeners.forEach((listener) => listener(token));
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    listeners.forEach((listener) => listener(null));
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Returns the raw text instead of parsed JSON (used by the CSV export). */
  raw?: boolean;
  signal?: AbortSignal;
}

const parseErrorMessage = (status: number, body: unknown): string => {
  if (typeof body === 'string' && body) return body;
  if (body && typeof body === 'object') {
    const message = (body as { message?: string | string[] }).message;
    if (Array.isArray(message)) return message.join('. ');
    if (message) return message;
    const description = (body as { error_description?: string }).error_description;
    if (description) return description;
  }
  return `Request failed with status ${status}`;
};

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const headers: Record<string, string> = {};
  const token = auth.token;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 401 && token) {
    auth.clear();
    throw new ApiError(401, 'Your session expired. Please sign in again.');
  }

  if (options.raw) {
    const text = await response.text();
    if (!response.ok) throw new ApiError(response.status, parseErrorMessage(response.status, text));
    return text as unknown as T;
  }

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) {
    throw new ApiError(response.status, parseErrorMessage(response.status, body), body);
  }
  return body as T;
};

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiRequest<T>(path, { signal }),
  getRaw: (path: string) => apiRequest<string>(path, { raw: true }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/** Builds `?a=1&b=2`, dropping empty values so filters stay out of the URL when unset. */
export const query = (params: Record<string, string | number | boolean | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
};

/** Unauthenticated POST used by the two endpoints that hand out a session. */
const startSession = async (path: string, body: unknown): Promise<void> => {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(response.status, parseErrorMessage(response.status, parsed));
  }
  auth.set(parsed.accessToken as string);
};

export const login = (email: string, password: string): Promise<void> =>
  startSession('/auth/login', { email, password });

/** First run only: creates the owner account and signs in. */
export const setupFirstUser = (input: {
  name: string;
  email: string;
  password: string;
}): Promise<void> => startSession('/auth/setup', input);

export const fetchAuthStatus = async (): Promise<{ needsSetup: boolean; userCount: number }> => {
  const response = await fetch('/api/auth/status');
  if (!response.ok) throw new ApiError(response.status, 'Could not reach the server');
  return (await response.json()) as { needsSetup: boolean; userCount: number };
};
